import { useQueryClient } from '@tanstack/react-query'
import { ActionError, Button, Field, FormBlock, Input, ReadingColumn, toast } from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { isAcceptablePassword, useChangePassword } from '@/features/auth/api'
import { CODE_WINDOW_MS } from '@/lib/auth'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { accountKeys } from './api'
import { PASSWORD_MESSAGES } from './labels'

/**
 * Aba Senha: o único lugar em que a pessoa cria ou troca a senha por vontade própria (RN-AUT-06,
 * RN-AUT-07, RN-AUT-08; P-AUT-09 decidida pela alternativa; T48). O efeito fica escrito antes do
 * botão: as outras sessões são encerradas, os aplicativos conectados perdem o acesso e a pessoa
 * recebe um e-mail. A senha atual não é pedida a quem não tem senha nenhuma (P-MIG-43) nem nos 10
 * minutos depois de entrar com código, quando `PUT /me/password` a dispensa: é o caminho de quem
 * esqueceu a senha. A aba não oferece nada sozinha: a pessoa chega aqui quando quer.
 */

const EFFECT =
  'Ao salvar, a senha nova vale na hora. As outras sessões da sua conta são encerradas (este dispositivo continua) e os aplicativos conectados perdem o acesso. Você recebe um e-mail avisando da troca.'

const NO_PASSWORD_INTRO =
  'Sua conta ainda não tem senha: você entra com código. Se quiser, crie uma para entrar também com e-mail e senha.'

const CODE_WINDOW_INTRO =
  'Você entrou com código: nos 10 minutos seguintes, a senha nova não pede a atual.'

/** A API voltou a pedir a senha atual a quem a aba não pedia: os 10 minutos do código passaram. */
const CODE_WINDOW_CLOSED =
  'Passaram os 10 minutos depois do código. Informe a senha atual ou entre com código de novo.'

export function PasswordTab() {
  const session = useSession()
  const queryClient = useQueryClient()
  const change = useChangePassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext] = useState(false)
  const [touched, setTouched] = useState({ current: false, next: false })
  // A recusa da API vale até a pessoa mexer no campo.
  const [refused, setRefused] = useState<Record<string, string>>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  // O relógio da janela do código é lido uma vez, ao abrir a aba; se a API recusar depois, fecha.
  const [openedAt] = useState(() => Date.now())
  const [isCodeWindowClosed, setIsCodeWindowClosed] = useState(false)

  const profile = session.profile
  // Sem senha nenhuma (nem definitiva, nem temporária), não há senha atual a informar.
  const hasNoPassword = profile ? !profile.hasPassword && !profile.hasTemporaryPassword : false
  const isWithinCodeWindow =
    !isCodeWindowClosed &&
    session.codeSignInAt !== null &&
    openedAt - session.codeSignInAt < CODE_WINDOW_MS
  const asksCurrent = !hasNoPassword && !isWithinCodeWindow

  const errors = {
    current: asksCurrent && current === '' ? PASSWORD_MESSAGES.current : null,
    next: !isAcceptablePassword(next)
      ? PASSWORD_MESSAGES.weak
      : asksCurrent && next === current
        ? PASSWORD_MESSAGES.same
        : null,
  }
  const currentError = refused.currentPassword ?? (touched.current ? errors.current : null)
  const nextError = refused.newPassword ?? (touched.next ? errors.next : null)
  const canSubmit = !errors.current && !errors.next

  async function submit(event: FormEvent) {
    event.preventDefault()
    setTouched({ current: true, next: true })
    if (!canSubmit || change.isPending) return
    setGeneralError(null)
    try {
      await change.mutateAsync(
        asksCurrent ? { currentPassword: current, newPassword: next } : { newPassword: next },
      )
    } catch (error) {
      const fields = fieldErrors(error)
      if (isWithinCodeWindow && fields.currentPassword) {
        // A janela do código fechou antes de salvar: o campo da senha atual aparece, com a causa.
        setIsCodeWindowClosed(true)
        setRefused({ ...fields, currentPassword: CODE_WINDOW_CLOSED })
        return
      }
      setRefused(fields)
      // Erro que não é de nenhum dos dois campos (limite de tentativas, rede) aparece inteiro.
      if (!fields.currentPassword && !fields.newPassword) setGeneralError(errorMessage(error))
      return
    }
    toast('Senha salva. As outras sessões foram encerradas.')
    setCurrent('')
    setNext('')
    setTouched({ current: false, next: false })
    // As outras sessões acabaram e os aplicativos perderam o acesso: as listas são refeitas.
    void queryClient.invalidateQueries({ queryKey: accountKeys.all })
  }

  return (
    <ReadingColumn>
      <form onSubmit={(event) => void submit(event)} noValidate className="flex flex-col gap-4">
        <FormBlock
          title="Nova senha"
          note={
            hasNoPassword
              ? `${NO_PASSWORD_INTRO} ${EFFECT}`
              : isWithinCodeWindow
                ? `${CODE_WINDOW_INTRO} ${EFFECT}`
                : EFFECT
          }
        >
          <div className="flex flex-col gap-4">
            {/* O gerenciador de senhas guarda a senha nova junto do e-mail da conta. */}
            {profile ? (
              <input type="email" autoComplete="username" value={profile.email} readOnly hidden />
            ) : null}

            {asksCurrent ? (
              <Field
                label="Senha atual"
                error={currentError}
                meta={
                  <ShowToggle
                    shown={showCurrent}
                    onToggle={() => setShowCurrent((value) => !value)}
                    what="a senha atual"
                  />
                }
              >
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={current}
                  onChange={(event) => {
                    setCurrent(event.target.value)
                    setRefused(({ currentPassword: _drop, ...rest }) => rest)
                  }}
                  onBlur={() => setTouched((state) => ({ ...state, current: true }))}
                />
              </Field>
            ) : null}

            <Field
              label="Nova senha"
              error={nextError}
              hint="Pelo menos 8 caracteres, com letras e números."
              meta={
                <ShowToggle
                  shown={showNext}
                  onToggle={() => setShowNext((value) => !value)}
                  what="a nova senha"
                />
              }
            >
              <Input
                type={showNext ? 'text' : 'password'}
                autoComplete="new-password"
                value={next}
                onChange={(event) => {
                  setNext(event.target.value)
                  setRefused(({ newPassword: _drop, ...rest }) => rest)
                }}
                onBlur={() => setTouched((state) => ({ ...state, next: true }))}
              />
            </Field>

            {generalError ? (
              <ActionError
                title="Não foi possível salvar a senha"
                description={generalError}
                onDismiss={() => setGeneralError(null)}
              />
            ) : null}

            <div className="flex flex-wrap items-center gap-4">
              <Button type="submit" loading={change.isPending} loadingLabel="Salvando…">
                Salvar nova senha
              </Button>
            </div>
          </div>
        </FormBlock>

        {asksCurrent ? (
          <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
            Esqueceu a senha atual? Saia, entre com código e volte aqui em até 10 minutos: a senha
            atual não é pedida.
          </p>
        ) : null}
      </form>
    </ReadingColumn>
  )
}

/** "Mostrar" · "Ocultar" no canto do rótulo, como no login. */
function ShowToggle({
  shown,
  onToggle,
  what,
}: {
  shown: boolean
  onToggle: () => void
  what: string
}) {
  return (
    <Button
      type="button"
      variant="text"
      className="text-eyebrow"
      aria-label={shown ? `Ocultar ${what}` : `Mostrar ${what}`}
      onClick={onToggle}
    >
      {shown ? 'Ocultar' : 'Mostrar'}
    </Button>
  )
}
